import { useState } from "react";
import { Col, Container, Row } from "react-bootstrap";
import { useAppSelector } from "../../store";
import { CompanyType } from "../../models/company";
import CompanyInformation from "./CompanyInformation";
import CompanyBranding from "./CompanyBranding";
import CompanyLinks from "./CompanyLinks";
import CompanySocials from "./CompanySocials";
import CompanyDocuments from "./CompanyDocuments";

interface CompanyGeneralProps {
  company: CompanyType;
  originalCompany: CompanyType | null;
  isNew: boolean;
  setCompany: (company: CompanyType) => void;
  hasFieldChanged: (currentValue: any, originalValue: any) => boolean;
  ChangedIndicator: () => JSX.Element;
  readOnly?: boolean;
}

type Section = "information" | "branding" | "links" | "socials" | "documents";

const SECTIONS: { key: Section; label: string; icon: string }[] = [
  { key: "information", label: "Company Information", icon: "bi-building" },
  { key: "branding", label: "Branding", icon: "bi-palette" },
  { key: "links", label: "Links", icon: "bi-link-45deg" },
  { key: "socials", label: "Socials", icon: "bi-share" },
  { key: "documents", label: "Documents", icon: "bi-folder" },
];

function CompanyGeneral({
  company,
  originalCompany,
  isNew,
  setCompany,
  hasFieldChanged,
  ChangedIndicator,
  readOnly = false,
}: CompanyGeneralProps) {
  const [selectedSection, setSelectedSection] = useState<Section>("information");
  const userRoles = useAppSelector((state) => state.user.roles) ?? [];
  const canViewDocuments = userRoles.some((r) => r.startsWith("files-"));
  const canUploadDocuments = userRoles.includes("files-upload");

  // Normalize a value for comparison: strip nulls, empty strings, and empty objects/arrays
  // so that null vs undefined vs [] vs {} don't trigger false positives.
  const normalizeForCompare = (val: any): string => {
    return JSON.stringify(val, (_key, v) => {
      if (v === null || v === "" || v === undefined) return undefined;
      if (Array.isArray(v) && v.length === 0) return undefined;
      if (typeof v === "object" && !Array.isArray(v) && Object.keys(v).length === 0) return undefined;
      return v;
    });
  };

  const hasSectionChanges = (section: Section): boolean => {
    if (!company || !originalCompany) return false;
    switch (section) {
      case "information":
        return (
          company.name !== originalCompany.name ||
          company.status !== originalCompany.status ||
          normalizeForCompare(company.info) !== normalizeForCompare(originalCompany.info) ||
          normalizeForCompare(company.legacy_data) !== normalizeForCompare(originalCompany.legacy_data) ||
          normalizeForCompare(company.config_design_temp?.communication_style) !==
            normalizeForCompare(originalCompany.config_design_temp?.communication_style)
        );
      case "branding":
        return (
          normalizeForCompare(company.config_design_temp?.images) !==
            normalizeForCompare(originalCompany.config_design_temp?.images) ||
          normalizeForCompare(company.config_design_temp?.colors) !==
            normalizeForCompare(originalCompany.config_design_temp?.colors) ||
          normalizeForCompare(company.config_design_temp?.typography) !==
            normalizeForCompare(originalCompany.config_design_temp?.typography)
        );
      case "links":
        return (
          normalizeForCompare(company.config_design_temp?.links) !==
          normalizeForCompare(originalCompany.config_design_temp?.links)
        );
      case "socials":
        return (
          normalizeForCompare(company.config_design_temp?.socials) !==
          normalizeForCompare(originalCompany.config_design_temp?.socials)
        );
      case "documents":
        return false;
      default:
        return false;
    }
  };

  const commonProps = {
    company,
    originalCompany,
    setCompany,
    hasFieldChanged,
    ChangedIndicator,
    readOnly,
  };

  return (
    <Container fluid>
      <Row>
        <Col md={3} className="border-end">
          <div className="list-group">
            {SECTIONS.filter((s) => s.key !== "documents" || canViewDocuments).map((section) => (
              <button
                key={section.key}
                type="button"
                className={`list-group-item list-group-item-action d-flex align-items-center gap-2 ${
                  selectedSection === section.key ? "active" : ""
                }`}
                onClick={() => setSelectedSection(section.key)}
              >
                <i className={`bi ${section.icon}`}></i>
                <span>
                  {section.label}
                  {hasSectionChanges(section.key) && <ChangedIndicator />}
                </span>
              </button>
            ))}
          </div>
        </Col>
        <Col md={9}>
          <div className="mb-4">
            <h4 className="mb-3">{SECTIONS.find((s) => s.key === selectedSection)?.label}</h4>
          </div>
          {selectedSection === "information" && <CompanyInformation {...commonProps} isNew={isNew} />}
          {selectedSection === "branding" && <CompanyBranding {...commonProps} />}
          {selectedSection === "links" && <CompanyLinks {...commonProps} />}
          {selectedSection === "socials" && <CompanySocials {...commonProps} />}
          {selectedSection === "documents" && canViewDocuments && (
            <CompanyDocuments company={company} readOnly={!canUploadDocuments} />
          )}
        </Col>
      </Row>
    </Container>
  );
}

export default CompanyGeneral;
